import { useNavigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import { ChevronRight, Car, Users, Plus, Square, Accessibility, Hand } from "lucide-react";
import { KioskViewport, useAudioUnlock, useKioskSpeech } from "../../components/ui/kiosk-viewport";
import { KioskHeader } from "../../components/ui/kiosk-header";

const IDLE_TIMEOUT = 60;
const WELCOME_MESSAGE = "A quick reminder. Please pay for your parking at the kiosk before returning to your vehicle. Once you've paid, simply drive to the exit and the barrier will open automatically. If you have parked in a designated parking space, please select the appropriate option on screen.";

export default function KioskWelcome() {
  const navigate = useNavigate();
  const idleTimerRef = useRef(null);
  
  const audioUnlocked = useAudioUnlock();
  const { needsInteraction, speak: triggerSpeak } = useKioskSpeech('KioskWelcome', WELCOME_MESSAGE, audioUnlocked);

  useEffect(() => {
    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => navigate('/kiosk/attract'), IDLE_TIMEOUT * 1000);
    };
    const events = ['mousedown', 'touchstart', 'keydown'];
    events.forEach(e => document.addEventListener(e, resetIdleTimer, { passive: true }));
    resetIdleTimer();
    return () => {
      events.forEach(e => document.removeEventListener(e, resetIdleTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [navigate]);

  return (
    <KioskViewport>
      <div 
        data-testid="kiosk-welcome"
        style={{ 
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: "'Outfit', 'Inter', system-ui, sans-serif",
          overflow: 'hidden',
          background: '#0d2137'
        }}
      >
        <KioskHeader showDateTime={true} />

        {/* HERO SECTION - Upper portion with Tracy and speech bubble */}
        <div style={{
          position: 'relative',
          height: '32vh',
          minHeight: '280px',
          maxHeight: '360px',
          overflow: 'hidden'
        }}>
          {/* Cityscape Background */}
          <img 
            src="/cityscape.png" 
            alt="Ayr Town Centre"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center center'
            }}
          />

          {/* P Sign */}
          <img 
            src="/psign.png" 
            alt="Parking Sign"
            style={{
              position: 'absolute',
              top: '15px',
              right: '280px',
              height: '120px',
              zIndex: 2
            }}
          />

          {/* Tracy - ParkWise Assistant */}
          <img 
            src="/tracy.png" 
            alt="Tracy - ParkWise Assistant"
            style={{
              position: 'absolute',
              right: '-20px',
              bottom: '-80px',
              height: '440px',
              zIndex: 10
            }}
          />

          {/* Speech Bubble with updated messaging */}
          <div style={{
            position: 'absolute',
            top: '15px',
            left: '40px',
            width: '480px',
            background: 'white',
            borderRadius: '24px',
            padding: '24px 28px',
            boxShadow: '0 12px 48px rgba(0,0,0,0.2)',
            zIndex: 20
          }}>
            <h2 style={{
              fontSize: '34px',
              fontWeight: '800',
              color: '#1e3a5f',
              margin: '0 0 4px 0',
              lineHeight: 1.1
            }}>
              Hello, I&apos;m ParkWise.
            </h2>
            <div style={{ 
              width: '70px', 
              height: '4px', 
              background: '#3b82f6', 
              borderRadius: '2px',
              margin: '10px 0 12px 0' 
            }} />
            <p style={{
              fontSize: '22px',
              color: '#1e3a5f',
              lineHeight: 1.4,
              margin: '0 0 4px 0',
              fontWeight: '500'
            }}>
              Most visitors only need to
            </p>
            <p style={{
              fontSize: '26px',
              color: '#22c55e',
              lineHeight: 1.3,
              margin: '0 0 12px 0',
              fontWeight: '800'
            }}>
              PAY BEFORE RETURNING TO YOUR VEHICLE.
            </p>
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
              padding: '10px 12px',
              background: '#f0f9ff',
              borderRadius: '10px',
              border: '2px solid #bfdbfe'
            }}>
              <div style={{
                width: '28px',
                height: '28px',
                background: '#3b82f6',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                marginTop: '2px'
              }}>
                <span style={{ color: 'white', fontWeight: '700', fontSize: '16px' }}>i</span>
              </div>
              <p style={{
                fontSize: '17px',
                color: '#1e40af',
                margin: 0,
                fontWeight: '600',
                lineHeight: 1.4
              }}>
                Please pay at the kiosk before returning to your vehicle. Once payment is complete, drive to the exit and the barrier will open automatically.
              </p>
            </div>
            {/* Pointer */}
            <div style={{
              position: 'absolute',
              right: '-22px',
              top: '60px',
              width: 0,
              height: 0,
              borderTop: '18px solid transparent',
              borderBottom: '18px solid transparent',
              borderLeft: '24px solid white'
            }} />
          </div>
        </div>

        {/* ACTION CARDS - Upper two-thirds for 65" portrait kiosk ergonomics */}
        <div style={{
          padding: '20px 40px 16px 40px',
          background: 'linear-gradient(180deg, #f8fafc 0%, #e0f2fe 100%)',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}>
          {/* PRIMARY ACTION - PAY BEFORE RETURNING TO YOUR VEHICLE - DOMINANT */}
          <button
            onClick={() => navigate("/kiosk/pay")}
            data-testid="kiosk-pay"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '20px',
              background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
              borderRadius: '24px',
              padding: '24px 28px',
              border: 'none',
              boxShadow: '0 10px 40px rgba(34,197,94,0.4)',
              cursor: 'pointer',
              textAlign: 'left'
            }}
          >
            {/* Car Icon */}
            <div style={{
              width: '80px',
              height: '80px',
              background: 'rgba(255,255,255,0.25)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <Car size={44} color="white" strokeWidth={2.5} />
            </div>
            
            {/* Text Content */}
            <div style={{ flex: 1 }}>
              <h3 style={{ fontSize: '34px', fontWeight: '800', color: 'white', margin: '0 0 6px 0', lineHeight: 1.1 }}>
                PAY BEFORE RETURNING TO YOUR VEHICLE
              </h3>
              <p style={{ fontSize: '22px', color: 'rgba(255,255,255,0.95)', margin: '0 0 4px 0', fontWeight: '600' }}>
                Parked in a standard parking space?
              </p>
              <p style={{ fontSize: '19px', color: 'rgba(255,255,255,0.85)', margin: 0, fontWeight: '500' }}>
                Please pay at the kiosk before returning to your vehicle. Once payment is complete, drive to the exit and the barrier will open automatically.
              </p>
            </div>

            {/* Touch/Press Here Section */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 16px',
              borderLeft: '2px solid rgba(255,255,255,0.3)',
              marginLeft: '8px'
            }}>
              <Hand size={48} color="white" strokeWidth={2} />
              <span style={{ 
                color: 'white', 
                fontWeight: '800', 
                fontSize: '18px',
                marginTop: '6px',
                textAlign: 'center'
              }}>
                PRESS<br/>HERE
              </span>
              <ChevronRight size={36} color="white" strokeWidth={3} style={{ marginTop: '4px' }} />
            </div>
          </button>

          {/* Visual separator with clearer hierarchy */}
          <div style={{ height: '4px' }} />

          {/* SECONDARY ACTIONS ROW - Clearly subordinate to primary */}
          <div style={{ display: 'flex', gap: '16px' }}>
            {/* REGISTER FOR A DESIGNATED SPACE Card */}
            <button
              onClick={() => navigate("/kiosk/category")}
              data-testid="kiosk-register"
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                background: 'white',
                borderRadius: '20px',
                padding: '20px 16px',
                border: '3px solid #3b82f6',
                boxShadow: '0 6px 24px rgba(59,130,246,0.2)',
                cursor: 'pointer',
                textAlign: 'center'
              }}
            >
              <div style={{
                width: '60px',
                height: '60px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '10px'
              }}>
                <Users size={32} color="white" strokeWidth={2.5} />
              </div>
              <h3 style={{ fontSize: '24px', fontWeight: '800', color: '#3b82f6', margin: '0 0 8px 0', lineHeight: 1.2 }}>
                REGISTER FOR A DESIGNATED SPACE
              </h3>
              <p style={{ fontSize: '17px', color: '#475569', margin: '0 0 12px 0', fontWeight: '600', lineHeight: 1.3 }}>
                This option is only for vehicles parked in one of the following designated spaces.
              </p>
              
              {/* Category Chips */}
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '8px', 
                justifyContent: 'center',
                marginBottom: '12px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '6px 12px',
                  background: '#dbeafe',
                  borderRadius: '16px'
                }}>
                  <Accessibility size={16} color="#2563eb" />
                  <span style={{ fontSize: '14px', fontWeight: '700', color: '#1e40af' }}>Blue Badge</span>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '6px 12px',
                  background: '#dbeafe',
                  borderRadius: '16px'
                }}>
                  <Users size={16} color="#2563eb" />
                  <span style={{ fontSize: '14px', fontWeight: '700', color: '#1e40af' }}>Adult with Child</span>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '6px 12px',
                  background: '#dbeafe',
                  borderRadius: '16px'
                }}>
                  <Plus size={16} color="#2563eb" />
                  <span style={{ fontSize: '14px', fontWeight: '700', color: '#1e40af' }}>NHS Staff</span>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '6px 12px',
                  background: '#dbeafe',
                  borderRadius: '16px'
                }}>
                  <Square size={16} color="#2563eb" />
                  <span style={{ fontSize: '14px', fontWeight: '700', color: '#1e40af' }}>Extra-Wide</span>
                </div>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: '#3b82f6',
                fontWeight: '700',
                fontSize: '18px'
              }}>
                I have parked in one of these spaces <ChevronRight size={22} />
              </div>
            </button>

            {/* MANAGE A PREVIOUS DESIGNATED REGISTRATION Card */}
            <button
              onClick={() => navigate("/kiosk/lookup")}
              data-testid="kiosk-manage"
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                background: 'white',
                borderRadius: '20px',
                padding: '20px 16px',
                border: '3px solid #7c3aed',
                boxShadow: '0 6px 24px rgba(124,58,237,0.2)',
                cursor: 'pointer',
                textAlign: 'center'
              }}
            >
              <div style={{
                width: '60px',
                height: '60px',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '10px'
              }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="16" rx="2" />
                  <line x1="7" y1="8" x2="17" y2="8" />
                  <line x1="7" y1="12" x2="13" y2="12" />
                </svg>
              </div>
              <h3 style={{ fontSize: '24px', fontWeight: '800', color: '#7c3aed', margin: '0 0 8px 0', lineHeight: 1.2 }}>
                MANAGE A PREVIOUS DESIGNATED REGISTRATION
              </h3>
              <p style={{ fontSize: '17px', color: '#475569', margin: '0 0 12px 0', fontWeight: '600', lineHeight: 1.3 }}>
                This option is only for vehicles that are already registered for a designated space.
              </p>
              <p style={{ fontSize: '18px', color: '#64748b', margin: '0 0 16px 0', fontWeight: '500', lineHeight: 1.4 }}>
                Check or update your vehicle details.
              </p>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: '#7c3aed',
                fontWeight: '700',
                fontSize: '18px'
              }}>
                I am already registered <ChevronRight size={22} />
              </div>
            </button>
          </div>

          {/* Helpful reminder for standard parkers */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '12px 16px',
            background: '#fef9c3',
            borderRadius: '12px',
            border: '2px solid #fde047'
          }}>
            <div style={{
              width: '28px',
              height: '28px',
              background: '#eab308',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <span style={{ color: 'white', fontWeight: '800', fontSize: '16px' }}>!</span>
            </div>
            <p style={{
              fontSize: '17px',
              color: '#854d0e',
              margin: 0,
              fontWeight: '600'
            }}>
              Not parked in a designated space? You only need to pay before returning to your vehicle.
            </p>
          </div>
        </div>

        {/* Street scene continuation - lower third background */}
        <div style={{ 
          flex: 1, 
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Continued street scene - existing artwork */}
          <img 
            src="/cityscape.png" 
            alt=""
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: '-20%',
              left: 0,
              width: '100%',
              height: '150%',
              objectFit: 'cover',
              objectPosition: 'center bottom',
              opacity: 0.65,
              filter: 'saturate(0.85) brightness(0.95)'
            }}
          />
          {/* Gradient overlay for smooth transition */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(180deg, rgba(224,242,254,0.9) 0%, rgba(224,242,254,0.3) 25%, rgba(13,33,55,0.4) 75%, rgba(13,33,55,0.95) 100%)'
          }} />
        </div>
      </div>
    </KioskViewport>
  );
}
